// pages/features/MatchingFeaturePage.jsx
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeHeader from "../modals/HomeHeader.js";
import { useCurrentUser } from "../APIs/UserContext";
import { getPatientPreference } from "../APIs/patientService";
import keycloak from "../APIs/keycloak";

export default function MatchingFeaturePage() {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const [checking, setChecking] = useState(false);

  const handleMatch = async () => {
    if (!currentUser) {
      keycloak.login({
        redirectUri: window.location.origin + "/ProviderMatchQuestions",
      });
      return;
    }

    if (currentUser.userType === "PATIENT" && currentUser.patientId) {
      setChecking(true);
      try {
        const prefs = await getPatientPreference(currentUser.patientId);
        if (prefs) {
          navigate("/PatientProfile?tab=patient preference");
          return;
        }
      } catch {
        // no preferences found — fall through
      } finally {
        setChecking(false);
      }
    }

    navigate("/ProviderMatchQuestions");
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <HomeHeader />

      {/* HERO */}
      <section className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
              Intelligent Matching
            </h1>

            <p className="mt-3 text-lg text-gray-600 leading-relaxed">
              Find providers that truly align with patient needs—based on
              preferences, availability, and relevance, not just proximity or
              network.
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              <Pill label="Specialty" />
              <Pill label="Availability" accent="emerald" />
              <Pill label="Insurance" />
              <Pill label="Location" />
              <Pill label="Preferences" accent="teal" />
            </div>

            <div className="mt-8">
              <button
                type="button"
                onClick={handleMatch}
                disabled={checking}
                className="inline-flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-full px-6 py-3 text-sm transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {checking
                  ? "Checking your preferences…"
                  : "Let's match you with the Provider that best suits you →"}
              </button>
              <p className="mt-2 text-xs text-gray-500">
                Takes ~2 minutes. Personalized results based on your needs.
              </p>
            </div>
          </div>

          {/* Match Results Illustration */}
          <div className="rounded-2xl border-2 border-teal-400 bg-white shadow-lg overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="font-semibold text-gray-800">
                Your Top Matches
              </div>
              <div className="text-xs text-gray-400">3 providers found</div>
            </div>

            <div className="p-4 space-y-3">
              {/* #1 — Best Match */}
              <div className="rounded-xl border-2 border-teal-400 bg-teal-50/60 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-full bg-teal-700 flex items-center justify-center text-xs font-bold text-white">
                      AJ
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-900">
                        Dr. Amina Johnson
                      </p>
                      <p className="text-xs text-gray-500">
                        Neurosurgery · Boston, MA
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center rounded-full bg-teal-600 px-2.5 py-1 text-[11px] font-bold text-white">
                    Best Match
                  </span>
                </div>

                <div className="mt-3.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-600">
                      Match Score
                    </span>
                    <span className="text-sm font-bold text-teal-700">96%</span>
                  </div>
                  <div className="h-2.5 w-full rounded-full bg-gray-200">
                    <div
                      className="h-2.5 rounded-full bg-teal-500"
                      style={{ width: "96%" }}
                    />
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  <Tag label="Accepts BlueCross" accent="teal" />
                  <Tag label="2.4 mi away" />
                  <Tag label="Sun 1–4 PM open" accent="teal" />
                </div>
              </div>

              {/* #2 — Compact */}
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 rounded-full bg-teal-100 border border-teal-200 flex items-center justify-center text-[11px] font-bold text-teal-700">
                  SO
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    Dr. Samuel Okafor
                  </p>
                  <p className="text-xs text-gray-500">
                    Neurosurgery · Cambridge, MA
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold text-teal-700">88%</p>
                  <div className="mt-0.5 h-1.5 w-16 rounded-full bg-gray-200">
                    <div
                      className="h-1.5 rounded-full bg-teal-400"
                      style={{ width: "88%" }}
                    />
                  </div>
                </div>
              </div>

              {/* #3 — Compact */}
              <div className="rounded-xl border border-gray-200 bg-white px-4 py-3 flex items-center gap-3">
                <div className="h-9 w-9 shrink-0 rounded-full bg-teal-100 border border-teal-200 flex items-center justify-center text-[11px] font-bold text-teal-700">
                  MC
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">
                    Dr. Michelle Chen
                  </p>
                  <p className="text-xs text-gray-500">
                    Neurology · Brookline, MA
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-bold text-teal-700">84%</p>
                  <div className="mt-0.5 h-1.5 w-16 rounded-full bg-gray-200">
                    <div
                      className="h-1.5 rounded-full bg-teal-400"
                      style={{ width: "84%" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONFIDENCE, NOT GUESSWORK — 2-col: text left, provider mock right */}
      <section className="bg-gradient-to-br from-teal-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Text */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Confidence, not guesswork
            </h2>
            <div className="mt-4 space-y-3 text-gray-700 leading-relaxed">
              <p>
                Patients want confidence—not guesswork—when choosing care.
                MedMaze's intelligent matching helps patients discover providers
                who align with their needs by considering meaningful attributes
                such as specialty, location, insurance, preferences, and
                availability.
              </p>
              <p>
                Rather than relying on a single filter or network status,
                MedMaze evaluates multiple factors together to surface providers
                who are both relevant and accessible.
              </p>
            </div>
          </div>

          {/* Provider results mock — fictional names */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden divide-y divide-gray-100">
            <ProviderMatchRow
              name="Dr. Priya Nair"
              specialty="Family Medicine"
              location="Houston, TX"
              score={70}
            />
            <ProviderMatchRow
              name="Dr. Marcus Webb"
              specialty="Family Medicine"
              location="Houston, TX"
              score={70}
            />
            <ProviderMatchRow
              name="Dr. Aisha Okonkwo"
              specialty="Family Medicine"
              location="Houston, TX"
              score={68}
            />
            <ProviderMatchRow
              name="Dr. Leo Carvalho"
              specialty="Family Medicine"
              location="Houston, TX"
              score={65}
            />
            <ProviderMatchRow
              name="Dr. Sana Mirza"
              specialty="Family Medicine"
              location="Houston, TX"
              score={65}
            />
          </div>
        </div>
      </section>

      {/* PATIENT + PROVIDER OUTCOMES */}
      <section className="bg-white py-4 pb-16">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Patient panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Designed for patient confidence
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Search and filter providers using meaningful criteria.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Answer a short set of questions to clarify preferences.
                </span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  See providers ranked by relevance—not just availability.
                </span>
              </li>
            </ul>
            <div className="mt-5 rounded-xl border border-teal-100 bg-teal-50 p-4">
              <p className="text-xs font-semibold text-teal-700">Note</p>
              <p className="mt-1 text-sm text-gray-700">
                Matching helps patients compare options with clarity—so
                decisions feel informed and personal.
              </p>
            </div>
          </div>

          {/* Provider panel */}
          <div className="rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-7 bg-gradient-to-br from-teal-50 to-white">
            <h3 className="text-xl font-bold text-gray-900">
              Fair, relevant discovery for providers
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-gray-700 leading-relaxed">
              <li className="flex gap-3">
                <BulletIcon />
                <span>Get matched to patients seeking your expertise.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>Appear based on real attributes—not paid placement.</span>
              </li>
              <li className="flex gap-3">
                <BulletIcon />
                <span>
                  Reach patients who are actively aligned with your care
                  offerings.
                </span>
              </li>
            </ul>
            <div className="mt-5 rounded-xl border border-teal-100 bg-teal-50 p-4">
              <p className="text-xs font-semibold text-teal-700">Result</p>
              <p className="mt-1 text-sm text-gray-700">
                Better alignment saves time on both sides and improves the
                quality of connections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* HELP STRIP */}
      <section className="bg-teal-50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-6 sm:p-8">
            <h3 className="text-lg font-bold text-gray-900">
              Why this matters
            </h3>
            <p className="mt-2 text-sm text-gray-700 leading-relaxed">
              By focusing on alignment instead of guesswork, MedMaze transforms
              how patients and providers find each other—making access to care
              more intentional and effective.
            </p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-white py-8 text-xs text-gray-500 text-center border-t">
        © {new Date().getFullYear()} MedMaze. All rights reserved.
      </footer>
    </div>
  );
}

function Pill({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold";
  const theme =
    accent === "teal"
      ? "border-teal-300 bg-teal-50 text-teal-700"
      : accent === "emerald"
        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
        : "border-teal-200 bg-teal-50 text-teal-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function Tag({ label, accent }) {
  const base =
    "inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold border";
  const theme =
    accent === "teal"
      ? "border-teal-200 bg-teal-50 text-teal-700"
      : "border-gray-200 bg-white text-gray-700";

  return <span className={`${base} ${theme}`}>{label}</span>;
}

function BulletIcon() {
  return (
    <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-100 text-teal-700">
      ✓
    </span>
  );
}

function ProviderMatchRow({ name, specialty, location, score }) {
  return (
    <div className="px-5 py-4 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="text-sm font-bold text-gray-900 truncate">{name}</p>
        <p className="text-xs text-gray-500">{specialty}</p>
        <p className="text-xs text-gray-400">{location}</p>
      </div>
      <span className="shrink-0 inline-flex items-center rounded-full bg-teal-50 border border-teal-200 px-2.5 py-1 text-xs font-semibold text-teal-700">
        Match Score: {score}%
      </span>
    </div>
  );
}
